import { useEffect, useState } from 'react'
import { api } from '@api'
import { SelectionItem } from '@utils'
import { FetchParams, PaginatedResponse } from './types'
import { usePaginate } from './usePaginate'

export interface City {
  id: string
  name: string
  nameEn: string
  nameFr: string
  nameEs?: string
  countryCode: string
  regionId: string | null
  // Absent from the answers of an older API.
  regionName?: string | null
  countryName?: string | null
}

/**
 * A city as a line of a picker: its region and country go under the name, so
 * cities that share a name can be told apart. Without the country name the
 * country code stands in.
 */
export const cityToSelectionItem = (city: City): SelectionItem => ({
  id: city.id,
  name: city.name ?? city.nameFr,
  text1: [city.regionName, city.countryName ?? city.countryCode].filter(Boolean).join(', ') || undefined,
})

const fetchCities = async ({
  pageParam = '1',
  country,
  countries,
  region,
  regionId,
  regionIds,
  continent,
  continents,
  lang,
  search,
  signal,
}: FetchParams): Promise<PaginatedResponse<City>> => {
  const params = new URLSearchParams()
  params.append('page', pageParam)
  if (country) {
    params.append('country', country)
  }
  if (countries?.length) {
    countries.forEach((c: string) => params.append('countries', c))
  }
  if (region) {
    params.append('region', region)
  }
  // UI stores `residenceRegions` as UUIDs, so we support filtering cities by `regionId`.
  if (regionId) {
    params.append('regionId', regionId)
  }
  if (regionIds?.length) {
    regionIds.forEach((id: string) => params.append('regionIds', id))
  }
  if (continent) {
    params.append('continent', continent)
  }
  if (continents?.length) {
    continents.forEach((c: string) => params.append('continents', c))
  }
  if (lang) {
    params.append('lang', lang)
  }
  if (search) {
    params.append('search', search)
  }
  
  const { data } = await api.get<PaginatedResponse<City>>(`/cities?${params.toString()}`, { signal })
  return data
}

// The search reads the whole table of cities: it is asked once the person
// pauses, not at each letter.
export const CITY_SEARCH_DELAY_MS = 300

const useDelayedSearch = (search?: string) => {
  const [delayed, setDelayed] = useState(search)
  useEffect(() => {
    if (!search) {
      setDelayed(search)
      return
    }
    const timer = setTimeout(() => setDelayed(search), CITY_SEARCH_DELAY_MS)
    return () => clearTimeout(timer)
  }, [search])
  // An emptied box shows the full list again at once.
  return search ? delayed : search
}

export const usePaginateCities = ({
  country,
  countries,
  region,
  regionId,
  regionIds,
  continent,
  continents,
  lang,
  search,
}: {
  country?: string
  countries?: string[]
  region?: string
  regionId?: string
  regionIds?: string[]
  continent?: string
  continents?: string[]
  lang?: 'en' | 'fr' | 'es'
  search?: string
}) => {
  const delayedSearch = useDelayedSearch(search)
  return usePaginate<City>(
    'cities',
    fetchCities,
    { country, countries, region, regionId, regionIds, continent, continents, lang, search: delayedSearch },
    // A search replaced by the next one is abandoned instead of running to its end.
    { abortWhenReplaced: true },
  )
}

